import { Expose, Type, Transform } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument, DocRef, anyToDate, toDocRef, toDocRefArray } from './Models';
import type { TDRegulation } from './td';

export interface AssessmentScheduling {
    enabled: boolean
    cron: string | null
    available_standard_ref: DocRef | null
    next_run: Date | null
}

export interface RegulationWatchScheduling {
    enabled: boolean
    cron: string | null
    next_run: Date | null
    lookback_days: number | null
    /** MYAC-1308 — the schedule also runs the suggestions for every product.
     * `undefined` on every schedule saved before it, which the backend reads as
     * off: existing schedules unchanged. */
    suggestions_enabled?: boolean
    /** MYAC-1308 — the suggestion half's own due date, same cron as `next_run`;
     * `null` while the suggestions are not scheduled. */
    suggestions_next_run: Date | null
}

export interface TDScheduling {
    enabled: boolean
    cron: string | null
    next_run: Date | null
}

/**
 * Lien entre un projet MAC et un projet TraceX (Huly).
 *
 * Stocké sous `projects/{pid}.tracex_sync_config`. Le front ne lit que les
 * champs non opaques. `project_ref` reste opaque : seul `tracex-sync` sait
 * qu'il s'agit d'un `_id` Huly.
 */
export interface TracexSyncConfig {
    datasource_id: string
    project_ref: string
    project_identifier: string
    project_name: string
    auto_export_on_accept: boolean
    configured_at?: Date | null
    configured_by_uid?: string | null
}

export class Project extends _FirebaseDocument {
    @Expose()
    @Type(() => DocRef)
    public tenant: DocRef
    @Expose()
    public name: string
    @Expose()
    public type: 'qms' | 'td' | 'regulatory_monitoring' | undefined = undefined
    @Expose()
    public plan: string
    @Expose()
    public admins: string[]
    @Expose()
    public members: string[]
    @Expose()
    public project_company_name?: string
    @Expose()
    public product_name?: string
    @Expose()
    public td_regulation?: TDRegulation = 'unknown'
    @Expose()
    @Transform(anyToDate)
    public market_placement_date?: Date | null = null
    @Expose()
    public status?: 'active' | 'soft_deleted' | 'unknown'
    @Expose()
    @Transform(anyToDate)
    public deleted_at?: Date | null = null
    @Expose()
    @Transform(anyToDate)
    public purge_after?: Date | null = null
    @Expose()
    @Transform(toDocRef)
    public parent_qms_project?: DocRef | null
    @Expose()
    public parent_qms_project_name?: string | null
    /** MYAC-1148: TDs connected to this regulatory-watch project (many). */
    @Expose()
    @Transform(toDocRefArray)
    public connected_projects?: DocRef[] | null
    @Expose()
    public connected_project_names?: string[] | null
    @Expose()
    @Transform(({ obj, key }) => {
        const value = obj[key]
        if (!value || typeof value !== 'object') return undefined
        return {
            enabled: value.enabled ?? false,
            cron: value.cron ?? null,
            available_standard_ref: toDocRef({ key: 'available_standard_ref', obj: value }),
            next_run: anyToDate({ obj: value, key: 'next_run' }),
        } as AssessmentScheduling
    })
    public assessment_scheduling?: AssessmentScheduling
    @Expose()
    @Transform(({ obj, key }) => {
        const value = obj[key]
        if (!value || typeof value !== 'object') return undefined
        return {
            enabled: value.enabled ?? false,
            cron: value.cron ?? null,
            next_run: anyToDate({ obj: value, key: 'next_run' }),
            lookback_days: value.lookback_days ?? null,
            suggestions_enabled: value.suggestions_enabled ?? undefined,
            suggestions_next_run: anyToDate({ obj: value, key: 'suggestions_next_run' }) ?? null,
        } as RegulationWatchScheduling
    })
    public regulation_watch_scheduling?: RegulationWatchScheduling
    @Expose()
    @Transform(({ obj, key }) => {
        const value = obj[key]
        if (!value || typeof value !== 'object') return undefined
        return {
            enabled: value.enabled ?? false,
            cron: value.cron ?? null,
            next_run: anyToDate({ obj: value, key: 'next_run' }),
        } as TDScheduling
    })
    public td_scheduling?: TDScheduling
    @Expose()
    @Transform(({ obj, key }) => {
        const value = obj[key]
        if (!value || typeof value !== 'object') return undefined
        return {
            datasource_id: value.datasource_id ?? '',
            project_ref: value.project_ref ?? '',
            project_identifier: value.project_identifier ?? '',
            project_name: value.project_name ?? '',
            auto_export_on_accept: value.auto_export_on_accept ?? false,
            configured_at: anyToDate({ obj: value, key: 'configured_at' }) ?? null,
            configured_by_uid: value.configured_by_uid ?? null,
        } as TracexSyncConfig
    })
    public tracex_sync_config?: TracexSyncConfig
    collection(): string {
        return "projects";
    }
    static fromFirebase(o: DocumentSnapshot): Project | null {
        return _FirebaseDocument._fromFirebase(Project, o);
    }
}

export type ProjectType = 'qms' | 'td' | 'regulatory_monitoring'

export function projectRouteBase(type: ProjectType | string): string {
    if (type === 'qms') return '/qms'
    if (type === 'regulatory_monitoring') return '/regulatory-monitoring'
    return '/products'
}
export type ProjectCreationStatus = 'requested' | 'pending' | 'done' | 'failed'

export class ProjectCreationRequest extends _FirebaseDocument {
    @Expose()
    public status: ProjectCreationStatus
    @Expose()
    public name: string
    @Expose()
    public type: ProjectType

    @Expose()
    public admins: string[]
    @Expose()
    public members: string[]

    @Expose()
    @Type(() => DocRef)
    public tenant: DocRef | null

    collection(): string {
        return 'project_creation_requests'
    }

    static fromFirebase(o: DocumentSnapshot): ProjectCreationRequest | null {
        return _FirebaseDocument._fromFirebase<ProjectCreationRequest>(ProjectCreationRequest, o)
    }
}
